import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import { useIsBrandLive, useLiveRefetchInterval } from "@/lib/socket";
import type { FieldReport } from "@/types/fieldReport.type";

export const fieldReportsKey = (brandId: string | null) => ["fieldReports", brandId];

/**
 * The brand's field reports, newest first. A report lands whenever a run finishes, announced
 * by a `signals.report.filed` hint while the brand is live; polled on the curation cadence otherwise.
 */
export function useFieldReports(brandId: string | null) {
  const live = useIsBrandLive(brandId);
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_CURATION_MS);
  const query = useQuery({
    queryKey: fieldReportsKey(brandId),
    queryFn: async (): Promise<FieldReport[]> => {
      const { data } = await axiosInstance.get<{ reports: FieldReport[] }>(
        `market-signals/${brandId}/reports`,
      );
      return data.reports ?? [];
    },
    enabled: Boolean(brandId),
    refetchInterval,
    refetchOnWindowFocus: !live,
  });

  return {
    reports: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
